import React, { useMemo, useState } from "react";
import { X } from "lucide-react";

/**
 * MessagesPanelContent.jsx
 * Premium light dashboard style messages panel (Users / Sellers tabs).
 * Tailwind required.
 */

const MiniBar = ({ data }) => {
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="flex items-end gap-2 h-24">
      {data.map((d) => (
        <div key={d.day} className="flex flex-col items-center w-6">
          <div
            className="rounded-t-md w-full transition-all duration-300"
            style={{
              height: `${Math.round((d.count / max) * 100)}%`,
              background: "linear-gradient(180deg,#60a5fa,#3b82f6)",
              boxShadow: "0 4px 8px rgba(59,130,246,0.12)",
            }}
            title={`${d.day}: ${d.count}`}
          />
          <div className="text-[11px] text-gray-400 mt-2">{d.day}</div>
        </div>
      ))}
    </div>
  );
};

export const MessagesPanelContent = ({ closePanel }) => {
  const [activeTab, setActiveTab] = useState("users");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");
  const [sortBy, setSortBy] = useState("Newest");

  // ---------- Static sample data ----------
  const users = [
    { id: "u1", name: "Rahul", role: "user", status: "Pending", lastSeen: "2 min ago", unread: 1, messages: 6 },
    { id: "u2", name: "Sneha", role: "user", status: "Pending", lastSeen: "10 min ago", unread: 2, messages: 3 },
    { id: "u3", name: "Amit", role: "user", status: "Resolved", lastSeen: "1 hr ago", unread: 0, messages: 4 },
    { id: "u4", name: "Priya", role: "user", status: "Pending", lastSeen: "Yesterday", unread: 0, messages: 2 },
    { id: "u5", name: "Vishal Chaubey", role: "user", status: "Pending", lastSeen: "Online", unread: 5, messages: 12 },
  ];

  const sellers = [
    { id: "s1", name: "LiftLife Store", role: "seller", status: "Active", lastSeen: "5 min ago", unread: 0, messages: 8 },
    { id: "s2", name: "ABC Elevators", role: "seller", status: "Active", lastSeen: "30 min ago", unread: 1, messages: 5 },
    { id: "s3", name: "TopLift Vendor", role: "seller", status: "Active", lastSeen: "2 hr ago", unread: 0, messages: 3 },
    { id: "s4", name: "MegaMotors", role: "seller", status: "Inactive", lastSeen: "3 days ago", unread: 0, messages: 1 },
  ];

  // ---------- Derived stats ----------
  const totalUsers = users.length;
  const totalSellers = sellers.length;
  const totalUnread = users.reduce((s, u) => s + (u.unread || 0), 0) + sellers.reduce((s, v) => s + (v.unread || 0), 0);
  const totalResolved = users.filter((u) => u.status === "Resolved").length;

  // messages per day sample (for chart)
  const messagesPerDay = [
    { day: "Mon", count: 20 },
    { day: "Tue", count: 34 },
    { day: "Wed", count: 26 },
    { day: "Thu", count: 15 },
    { day: "Fri", count: 28 },
    { day: "Sat", count: 12 },
    { day: "Sun", count: 6 },
  ];

  // ---------- Items to display depending on tab ----------
  const items = useMemo(() => {
    const pool = activeTab === "users" ? users : sellers;
    const q = query.trim().toLowerCase();
    let filtered = pool.filter((p) => {
      if (!q) return true;
      return p.name.toLowerCase().includes(q) || (p.status || "").toLowerCase().includes(q);
    });

    if (filter !== "All") {
      if (filter === "Unread") filtered = filtered.filter((p) => (p.unread || 0) > 0);
      else if (filter === "Resolved") filtered = filtered.filter((p) => p.status === "Resolved");
      else if (filter === "Active") filtered = filtered.filter((p) => p.status === "Active");
      else if (filter === "Inactive") filtered = filtered.filter((p) => p.status === "Inactive");
    }

    if (sortBy === "Newest") filtered = filtered.slice();
    else if (sortBy === "Most Messages") filtered = filtered.slice().sort((a, b) => (b.messages || 0) - (a.messages || 0));

    return filtered;
  }, [activeTab, query, filter, sortBy]);

  return (
    <div className="w-full h-screen bg-gray-50">
      {/* Container */}
      <div className="max-w-[1200px] mx-auto p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-extrabold text-gray-800">Messages</h2>
            <p className="text-sm text-gray-500 mt-1">Admin overview — users, sellers & activity</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={closePanel}
              className="p-2 rounded-lg bg-white shadow-sm hover:shadow-md text-gray-600"
              aria-label="Close panel"
              title="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Top analytics */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
          <div className="p-4 rounded-xl bg-white shadow-sm hover:shadow-md transition">
            <p className="text-xs text-gray-400">Total Users</p>
            <div className="flex items-center justify-between mt-2">
              <h3 className="text-2xl font-semibold">{totalUsers}</h3>
              <div className="text-sm text-green-500 bg-green-50 px-2 py-1 rounded-md">+2.3%</div>
            </div>
            <p className="text-xs text-gray-400 mt-2">Active users on platform</p>
          </div>

          <div className="p-4 rounded-xl bg-white shadow-sm hover:shadow-md transition">
            <p className="text-xs text-gray-400">Total Sellers</p>
            <div className="flex items-center justify-between mt-2">
              <h3 className="text-2xl font-semibold">{totalSellers}</h3>
              <div className="text-sm text-purple-600 bg-purple-50 px-2 py-1 rounded-md">Stable</div>
            </div>
            <p className="text-xs text-gray-400 mt-2">Registered sellers</p>
          </div>

          <div className="p-4 rounded-xl bg-white shadow-sm hover:shadow-md transition">
            <p className="text-xs text-gray-400">Unread Messages</p>
            <div className="flex items-center justify-between mt-2">
              <h3 className="text-2xl font-semibold text-blue-600">{totalUnread}</h3>
              <div className="text-sm text-red-500 bg-red-50 px-2 py-1 rounded-md">Immediate</div>
            </div>
            <p className="text-xs text-gray-400 mt-2">Pending unread messages</p>
          </div>

          <div className="p-4 rounded-xl bg-white shadow-sm hover:shadow-md transition">
            <p className="text-xs text-gray-400">Resolved</p>
            <div className="flex items-center justify-between mt-2">
              <h3 className="text-2xl font-semibold text-green-600">{totalResolved}</h3>
              <div className="text-sm text-gray-500 bg-gray-50 px-2 py-1 rounded-md">Good</div>
            </div>
            <p className="text-xs text-gray-400 mt-2">Resolved user issues</p>
          </div>
        </div>

        {/* Chart + Controls */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <div className="col-span-2 p-4 rounded-xl bg-white shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Messages (last 7 days)</p>
                <h3 className="text-lg font-semibold text-gray-800">Activity Overview</h3>
              </div>
              <div className="text-xs text-gray-400">Total: {messagesPerDay.reduce((s, d) => s + d.count, 0)}</div>
            </div>

            <div className="mt-4">
              <MiniBar data={messagesPerDay} />
            </div>
          </div>

          <div className="p-4 rounded-xl bg-white shadow-sm">
            <p className="text-sm text-gray-500 mb-3">Quick Controls</p>

            <div className="space-y-3">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-200"
                placeholder={`Search ${activeTab === "users" ? "users" : "sellers"}...`}
              />

              <div className="flex gap-2">
                <select value={filter} onChange={(e) => setFilter(e.target.value)} className="flex-1 px-3 py-2 border rounded-lg text-sm">
                  <option>All</option>
                  <option>Unread</option>
                  <option>Resolved</option>
                  <option>Active</option>
                  <option>Inactive</option>
                </select>

                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="w-36 px-3 py-2 border rounded-lg text-sm">
                  <option>Newest</option>
                  <option>Most Messages</option>
                </select>
              </div>

              <div className="pt-3 border-t">
                <p className="text-xs text-gray-400">Summary</p>
                <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                  <div className="p-2 bg-gray-50 rounded-md">
                    <p className="text-xs text-gray-400">Active Users</p>
                    <p className="font-semibold">{users.filter((u) => u.status !== "Resolved").length}</p>
                  </div>
                  <div className="p-2 bg-gray-50 rounded-md">
                    <p className="text-xs text-gray-400">Resolved Users</p>
                    <p className="font-semibold">{users.filter((u) => u.status === "Resolved").length}</p>
                  </div>
                  <div className="p-2 bg-gray-50 rounded-md">
                    <p className="text-xs text-gray-400">Active Sellers</p>
                    <p className="font-semibold">{sellers.filter((s) => s.status === "Active").length}</p>
                  </div>
                  <div className="p-2 bg-gray-50 rounded-md">
                    <p className="text-xs text-gray-400">Total Messages</p>
                    <p className="font-semibold">{users.reduce((t, u) => t + u.messages, 0) + sellers.reduce((t, s) => t + s.messages, 0)}</p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => setActiveTab("users")}
            className={`px-4 py-2 rounded-full font-medium ${activeTab === "users" ? "bg-gradient-to-r from-indigo-600 to-violet-500 text-white shadow-md" : "bg-gray-100 text-gray-700"}`}
          >
            Users
          </button>

          <button
            onClick={() => setActiveTab("sellers")}
            className={`px-4 py-2 rounded-full font-medium ${activeTab === "sellers" ? "bg-gradient-to-r from-indigo-600 to-violet-500 text-white shadow-md" : "bg-gray-100 text-gray-700"}`}
          >
            Sellers
          </button>

          <div className="ml-auto text-xs text-gray-500">Showing: {activeTab === "users" ? `${totalUsers} users` : `${totalSellers} sellers`}</div>
        </div>

        {/* Messages list */}
        <div className="space-y-3">
          {items
            .map((p) => (
              <div key={p.id} className="flex items-center justify-between p-3 bg-white rounded-xl shadow-sm hover:shadow-md transition">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 font-semibold text-sm">
                    {p.name.split(" ").map(n => n[0]).slice(0,2).join("")}
                  </div>

                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-800">{p.name}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${p.role === "user" ? (p.status === "Resolved" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700") : "bg-indigo-100 text-indigo-700"}`}>
                        {p.role === "user" ? p.status : "Seller"}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{p.lastSeen} • {p.messages} msgs</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-800">{p.unread > 0 ? p.unread : ""}</p>
                    <p className="text-xs text-gray-400">unread</p>
                  </div>

                  <button className="px-3 py-1 rounded-lg bg-gray-100 text-sm text-gray-600">Open</button>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default MessagesPanelContent;
